import isEqual from "lodash/isEqual";
import { useCallback, useEffect, useMemo, useState } from "react";
import { updateArr } from "../../misc/utils";
import { copyFromCombination } from "./copyFromCombination";
import type { ParticipantsForCombination, Person } from "./getRoomsCells";
import type {
  AddParticipantListRequest,
  ParticipantListResponse,
  ParticipantListTemplateResponse,
} from "./participantListApi";
import type { DeepRequired } from "./participantListEditorHelpers";
import {
  lastSavedPayload,
  mergePersonChanges,
  normalizePayloadForComparison,
  stateForTemplate,
  toRequestPayload,
} from "./participantListEditorHelpers";

export interface UseParticipantListEditorResult {
  readonly combinationIdx: string;
  readonly setCombinationIdx: (idx: string) => void;
  readonly combinationsState: DeepRequired<ParticipantsForCombination>[];
  readonly plateNumber: string;
  readonly setPlateNumber: (v: string) => void;
  readonly driverPhone: string;
  readonly setDriverPhone: (v: string) => void;
  readonly tourLeaderPhone: string;
  readonly setTourLeaderPhone: (v: string) => void;
  readonly handleReset: () => void;
  readonly handleSubmit: () => Promise<void>;
  readonly handlePersonChange: (
    combinationIdx: number,
    roomType: string,
    roomIdx: number,
    personIdx: number,
    person: Partial<Person> | null,
  ) => void;
  readonly handleCopyFromFirstCombination: (combinationIdx: number) => void;
  readonly submitEnabled: boolean;
}

export function useParticipantListEditor({
  template,
  previousParticipantList,
  onAdd,
}: {
  template: ParticipantListTemplateResponse | undefined;
  previousParticipantList: Omit<ParticipantListResponse, "id" | "createdAt"> | undefined;
  onAdd: (request: AddParticipantListRequest) => Promise<void>;
}): UseParticipantListEditorResult {
  const [combinationIdx, setCombinationIdx] = useState("0");
  const [combinationsState, setCombinationsState] = useState<DeepRequired<ParticipantsForCombination>[]>(() =>
    template ? stateForTemplate(template, previousParticipantList) : [],
  );
  const [plateNumber, setPlateNumber] = useState(previousParticipantList?.bus?.plateNumber ?? "");
  const [driverPhone, setDriverPhone] = useState(previousParticipantList?.bus?.driverPhone ?? "");
  const [tourLeaderPhone, setTourLeaderPhone] = useState(previousParticipantList?.tourLeader?.tourLeaderPhone ?? "");

  useEffect(() => {
    if (!template) return;
    setCombinationsState(stateForTemplate(template, previousParticipantList));
    setPlateNumber(previousParticipantList?.bus?.plateNumber ?? "");
    setDriverPhone(previousParticipantList?.bus?.driverPhone ?? "");
    setTourLeaderPhone(previousParticipantList?.tourLeader?.tourLeaderPhone ?? "");
  }, [template, previousParticipantList]);

  const handleReset = useCallback(() => {
    if (!template) return;
    setCombinationsState(stateForTemplate(template, previousParticipantList));
    setPlateNumber("");
    setDriverPhone("");
    setTourLeaderPhone("");
  }, [template, previousParticipantList]);

  const handleSubmit = useCallback(async () => {
    if (!template) return;
    await onAdd({
      combinations: combinationsState.map((c) => {
        const result: Record<string, { people: (Person | null)[] }[]> = {};
        for (const [key, val] of Object.entries(c)) {
          result[key] = val.rooms;
        }
        return result;
      }),
      bus: template.isBusRequired ? { plateNumber, driverPhone } : undefined,
      tourLeader: { tourLeaderPhone: tourLeaderPhone || undefined },
    });
    setCombinationsState(stateForTemplate(template, previousParticipantList));
    setPlateNumber("");
    setDriverPhone("");
    setTourLeaderPhone("");
  }, [template, combinationsState, plateNumber, driverPhone, tourLeaderPhone, onAdd, previousParticipantList]);

  const handlePersonChange = useCallback(
    (
      combinationIdxArg: number,
      roomType: string,
      roomIdx: number,
      personIdx: number,
      person: Partial<Person> | null,
    ) => {
      setCombinationsState((s) => {
        const combination = s[combinationIdxArg];
        const rooms = combination[roomType].rooms.map((r, idx) =>
          idx === roomIdx
            ? { people: r.people.map((t, pIdx) => (pIdx === personIdx ? mergePersonChanges(t, person) : t)) }
            : r,
        );
        const updatedCombination = {
          ...combination,
          [roomType]: { rooms },
        };
        return updateArr(s, combinationIdxArg, updatedCombination);
      });
    },
    [],
  );

  const handleCopyFromFirstCombination = useCallback(
    (combinationIdxArg: number) =>
      setCombinationsState((s) => {
        if (!template) return s;
        const firstCombination = s[0];
        return updateArr(
          s,
          combinationIdxArg,
          copyFromCombination(firstCombination, template.combinations[combinationIdxArg]),
        );
      }),
    [template],
  );

  const savedPayload = useMemo(
    () => (template ? lastSavedPayload(previousParticipantList, template) : null),
    [previousParticipantList, template],
  );
  const currentPayload = template
    ? toRequestPayload(combinationsState, template, plateNumber, driverPhone, tourLeaderPhone)
    : null;
  const hasChanges =
    savedPayload !== null && currentPayload !== null
      ? !isEqual(normalizePayloadForComparison(currentPayload), normalizePayloadForComparison(savedPayload))
      : false;
  const submitEnabled = previousParticipantList ? hasChanges : true;

  return {
    combinationIdx,
    setCombinationIdx,
    combinationsState,
    plateNumber,
    setPlateNumber,
    driverPhone,
    setDriverPhone,
    tourLeaderPhone,
    setTourLeaderPhone,
    handleReset,
    handleSubmit,
    handlePersonChange,
    handleCopyFromFirstCombination,
    submitEnabled,
  };
}
